import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import allApi from "../../../../api/allApi";
import Loader from "../../common/Loader";
import Modal from "react-bootstrap/Modal";
import errorIcon from "../../../../assets/images/error-2.png";
import { useLocalization } from "../../localization/LocalizationContext";
import { useNetwork } from "../../common/networkContext";
import NmsTableSearch, { searchRows } from "../../common/NmsTableSearch";

function Reports() {
  const { localizationStrings } = useLocalization();
  const { selectedNetworkId, selectedLocation } = useNetwork();
  const networkId = selectedNetworkId?.id ?? 6001;
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState({
    name: "",
    description: "",
    widgets: "",
    status: "",
    createdAt: "",
  });
  const [tableQuery, setTableQuery] = useState("");
  const [sortOrder, setSortOrder] = useState({ column: "", order: "asc" });
  const [selectedReports, setSelectedReports] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 10;
  const [modalData, setModalData] = useState(null);
  const [modalData1, setModalData1] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpen1, setIsModalOpen1] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isSuccessMessageVisible, setIsSuccessMessageVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [reportToSend, setReportToSend] = useState(null);
  const [reportName, setReportName] = useState("");
  const [showDeleteSuccessModal, setShowDeleteSuccessModal] = useState(false);

  useEffect(() => {
    fetchReports(networkId);
  }, [networkId]);

  const fetchReports = async (networkId) => {
    setLoading(true);
    try {
      const api = new allApi();
      const response = await api.getRequest(`api/report/list?networkId=${networkId}`);
      if (response && Array.isArray(response)) {
        const sortedReports = response.sort(
          (a, b) => b.createdAt - a.createdAt
        );
        setReports(sortedReports);
        setFilteredReports(sortedReports);
        setSelectedReports([]);
        setSelectAll(false);
      } else {
        setReports([]);
        setFilteredReports([]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteModal = (report) => {
    if (report) {
      setReportToDelete(report.reportId); // For single report deletion
      setReportName(report.reportName);
    } else {
      setReportToDelete(null); // For multiple report deletion
    }
    setIsModalOpen1(true);
  };

  const handleCloseDeleteModal = (report) => {
    setReportToDelete(null);
    setReportName("");
    // setModalData1({ id });
    setIsModalOpen1(false);
  };

  const handleDeleteReport = async () => {
    if (!reportToDelete && selectedReports.length === 0) return;

    setLoading(true);
    try {
      const api = new allApi();
      const payload = reportToDelete
        ? { reportId: [reportToDelete] }
        : { reportId: selectedReports };
      const response = await api.deleteRequest(
        "api/report/deleteReports",
        payload
      );

      if (response) {
        fetchReports();
        handleCloseDeleteModal();
        setShowDeleteSuccessModal(true);
        setTimeout(() => setShowDeleteSuccessModal(false), 2000);
      }
    } catch (err) {
      console.error(`Error during deletion: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (reportId, reportName) => {
    try {
      const api = new allApi();
      const response = await api.getDownloadRequest(
        `api/report/download/${reportId}`,
        {
          responseType: "blob",
        }
      );

      if (!response) {
        throw new Error("No response received from the server.");
      }
      const blob = new Blob([response], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${reportName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setError("Failed to download the report.");
      console.error("Download error:", error);
    }
  };

  const handleViewReport = async (reportId) => {
    setLoading(true);
    try {
      const api = new allApi();
      const response = await api.getDownloadRequest(
        `api/report/view/${reportId}`,
        {
          responseType: "blob",
        }
      );

      if (!response) {
        throw new Error("Failed to fetch the report.");
      }

      if (response instanceof Blob) {
        const url = window.URL.createObjectURL(response);
        setModalData(url);
        setIsModalOpen(true);
      } else {
        throw new Error("Received non-PDF response.");
      }
    } catch (error) {
      console.error("View Report Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = (reportId, emailRecipients) => {
    if (!emailRecipients || !Array.isArray(emailRecipients)) {
      console.error("Invalid report data: ", emailRecipients);
      setEmail("No recipients available.");
    } else {
      const recipients = emailRecipients.join(", ");
      setEmail(recipients);
    }
    setReportToSend(reportId);
    setIsConfirmationModalOpen(true);
  };

  const handleConfirmSendEmail = async () => {
    if (!reportToSend) return;

    setLoading(true);
    try {
      const api = new allApi();
      const payload = {
        reportId: reportToSend,
      };
      const response = await api.postRequest(
        "api/report/send-report",
        "",
        payload
      );
      if (response) {
        setIsSuccessMessageVisible(true);
        setTimeout(() => {
          setIsSuccessMessageVisible(false);
        }, 3000);
        setIsConfirmationModalOpen(false);
      }
    } catch (error) {
      console.error("Error sending report email:", error);
      alert("Error sending report email.");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalData(null);
  };

  const handleSearch = (e) => {
    const { name, value } = e.target;
    const updatedSearch = { ...searchTerm, [name]: value };
    setSearchTerm(updatedSearch);

    const allFieldsEmpty = Object.values(updatedSearch).every(
      (term) => term.trim() === ""
    );
    if (allFieldsEmpty) {
      setFilteredReports(reports);
      return;
    }

    const filtered = reports.filter((report) => {
      const matches = {
        name: report.reportName
          ?.toLowerCase()
          .includes(updatedSearch.name.toLowerCase()),
        description: report.description
          ?.toLowerCase()
          .includes(updatedSearch.description.toLowerCase()),
        widgets: report.widgetList
          ?.join(", ")
          .toLowerCase()
          .includes(updatedSearch.widgets.toLowerCase()),
        status: (report.reportStatus === 1 ? "success" : "pending")
          .toLowerCase()
          .includes(updatedSearch.status.toLowerCase()),
        createdAt: new Date(report.createdAt * 1000)
          .toLocaleDateString()
          .includes(updatedSearch.createdAt),
      };

      return Object.values(matches).every((match) => match);
    });

    setFilteredReports(filtered);
    setCurrentPage(0);
  };

  const handleSortChange = (column) => {
    const newOrder =
      sortOrder.column === column && sortOrder.order === "asc" ? "desc" : "asc";
    const sorted = [...filteredReports].sort((a, b) => {
      let valueA, valueB;

      if (column === "widgets") {
        valueA = a.widgetList?.join(", ").toLowerCase() || "";
        valueB = b.widgetList?.join(", ").toLowerCase() || "";
      } else if (column === "createdAt") {
        valueA = new Date(a.createdAt * 1000);
        valueB = new Date(b.createdAt * 1000);
      } else if (column === "status") {
        valueA = a.reportStatus === 1 ? "success" : "pending";
        valueB = b.reportStatus === 1 ? "success" : "pending";
      } else {
        valueA = a[column]?.toString().toLowerCase() || "";
        valueB = b[column]?.toString().toLowerCase() || "";
      }

      if (valueA < valueB) return newOrder === "asc" ? -1 : 1;
      if (valueA > valueB) return newOrder === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredReports(sorted);
    setSortOrder({ column, order: newOrder });
  };

  const handleSelectReport = (id) => {
    const updatedSelection = selectedReports.includes(id)
      ? selectedReports.filter((reportId) => reportId !== id)
      : [...selectedReports, id];
    setSelectedReports(updatedSelection);
    setSelectAll(updatedSelection.length === filteredReports.length);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedReports([]);
    } else {
      const allIds = filteredReports.map((report) => report.reportId);
      setSelectedReports(allIds);
    }
    setSelectAll(!selectAll);
  };

  const handlePageClick = (page) => {
    if (page >= 0 && page < totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];

    // Add previous page, current page, and next page
    if (currentPage > 0) {
      pageNumbers.push(currentPage - 1);
    }
    pageNumbers.push(currentPage);
    if (currentPage < totalPages - 1) {
      pageNumbers.push(currentPage + 1);
    }

    return pageNumbers;
  };

  const visibleReports = searchRows(filteredReports, tableQuery, [
    "reportName",
    "description",
    "widgetList",
    "reportStatus",
  ]);
  const totalPages = Math.ceil(visibleReports.length / pageSize);
  const paginatedReports = visibleReports.slice(
    currentPage * pageSize,
    (currentPage + 1) * pageSize
  );

  return (
    <div className="nms-dash">
      <div className="inc-card ch-100">
        <div className="inc-card-title">
          <h3>{localizationStrings?.reports}</h3>
          <div className="inc-card-button">
            <NmsTableSearch
              value={tableQuery}
              onChange={(value) => {
                setTableQuery(value);
                setCurrentPage(0);
              }}
              placeholder={localizationStrings?.search || "Search reports"}
            />
            <button
              className="text-btn primary-btn"
              role="button"
              onClick={() => navigate("/report-generate")}
            >
              <i className="fa fa-plus" aria-hidden="true"></i>{" "}
              {localizationStrings?.createReports}
            </button>
            <button className="icon-btn" title="Refresh" onClick={()=>fetchReports(networkId)}>
              <i className="fa fa-refresh" aria-hidden="true"></i>
            </button>
            <button
              className="icon-btn"
              title="Delete"
              disabled={selectedReports.length <= 1}
              onClick={() => handleOpenDeleteModal()}
            >
              <i className="fa fa-trash" aria-hidden="true"></i>
            </button>
          </div>
        </div>
        {isSuccessMessageVisible && (
          <div className="alert alert-success">
            {localizationStrings?.reportSuccess} {email}{" "}
            {localizationStrings?.successfully}
          </div>
        )}
        <div className="inc-card-body">
          <div className="tab-pagination">
            <div className="inc-card-table">
              <table className="full-table m-head action150">
                <thead>
                  <tr>
                    <th>
                      <label className="checkbox">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                        />
                        <span className="indicator"></span>
                      </label>
                    </th>
                    <th
                      className={`inc-th-sort${
                        sortOrder.column === "reportName" ? " is-active" : ""
                      }`}
                      onClick={() => handleSortChange("reportName")}
                    >
                      {localizationStrings?.reportName}
                      <i className="fa fa-sort" aria-hidden="true" />
                    </th>
                    <th
                      className={`inc-th-sort${
                        sortOrder.column === "description" ? " is-active" : ""
                      }`}
                      onClick={() => handleSortChange("description")}
                    >
                      {localizationStrings?.description}
                      <i className="fa fa-sort" aria-hidden="true" />
                    </th>
                    <th
                      className={`inc-th-sort${
                        sortOrder.column === "widgets" ? " is-active" : ""
                      }`}
                      onClick={() => handleSortChange("widgets")}
                    >
                      {localizationStrings?.widgets}
                      <i className="fa fa-sort" aria-hidden="true" />
                    </th>
                    <th
                      className={`inc-th-sort${
                        sortOrder.column === "createdAt" ? " is-active" : ""
                      }`}
                      onClick={() => handleSortChange("createdAt")}
                    >
                      {localizationStrings?.createdTime}
                      <i className="fa fa-sort" aria-hidden="true" />
                    </th>
                    <th
                      className={`inc-th-sort${
                        sortOrder.column === "status" ? " is-active" : ""
                      }`}
                      onClick={() => handleSortChange("status")}
                    >
                      {localizationStrings?.status}
                      <i className="fa fa-sort" aria-hidden="true" />
                    </th>
                    <th>{localizationStrings?.actions}</th>
                  </tr>
                  <tr className="search-border">
                    <td></td>
                    <td>
                      <input
                        className="searchBox smallWidth"
                        type="text"
                        name="name"
                        onKeyUp={handleSearch}
                      />
                      <i
                        className={`fa ${
                          sortOrder.column === "reportName" &&
                          sortOrder.order === "asc"
                            ? "fa fa-sort-amount-desc"
                            : "fa fa-sort-amount-asc"
                        }`}
                        onClick={() => handleSortChange("reportName")}
                        aria-hidden="true"
                      ></i>
                    </td>
                    <td>
                      <input
                        className="searchBox smallWidth"
                        type="text"
                        name="description"
                        onKeyUp={handleSearch}
                      />
                      <i
                        className={`fa ${
                          sortOrder.column === "description" &&
                          sortOrder.order === "asc"
                            ? "fa fa-sort-amount-desc"
                            : "fa fa-sort-amount-asc"
                        }`}
                        onClick={() => handleSortChange("description")}
                        aria-hidden="true"
                      ></i>
                    </td>
                    <td>
                      <input
                        className="searchBox smallWidth"
                        type="text"
                        name="widgets"
                        onKeyUp={handleSearch}
                      />
                      <i
                        className={`fa ${
                          sortOrder.column === "widgets" &&
                          sortOrder.order === "asc"
                            ? "fa fa-sort-amount-desc"
                            : "fa fa-sort-amount-asc"
                        }`}
                        onClick={() => handleSortChange("widgets")}
                        aria-hidden="true"
                      ></i>
                    </td>
                    <td>
                      <input
                        className="searchBox smallWidth"
                        type="text"
                        name="createdAt"
                        onKeyUp={handleSearch}
                      />
                      <i
                        className={`fa ${
                          sortOrder.column === "createdAt" &&
                          sortOrder.order === "asc"
                            ? "fa fa-sort-amount-desc"
                            : "fa fa-sort-amount-asc"
                        }`}
                        onClick={() => handleSortChange("createdAt")}
                        aria-hidden="true"
                      ></i>
                    </td>
                    <td>
                      <input
                        className="searchBox smallWidth"
                        type="text"
                        name="status"
                        onKeyUp={handleSearch}
                      />
                      <i
                        className={`fa ${
                          sortOrder.column === "status" &&
                          sortOrder.order === "asc"
                            ? "fa fa-sort-amount-desc"
                            : "fa fa-sort-amount-asc"
                        }`}
                        onClick={() => handleSortChange("status")}
                        aria-hidden="true"
                      ></i>
                    </td>
                    <td></td>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: "center" }}>
                        <Loader />
                      </td>
                    </tr>
                  ) : paginatedReports.length > 0 ? (
                    paginatedReports.map((report) => (
                      <tr key={report.reportId}>
                        <td>
                          <label className="checkbox">
                            <input
                              type="checkbox"
                              checked={selectedReports.includes(
                                report.reportId
                              )}
                              onChange={() =>
                                handleSelectReport(report.reportId)
                              }
                            />
                            <span className="indicator"></span>
                          </label>
                        </td>
                        <td>{report.reportName || "N/A"}</td>
                        <td>{report.description || "N/A"}</td>
                        <td>{report.widgetList?.join(", ") || "N/A"}</td>
                        <td>
                          {new Date(
                            report.createdAt * 1000
                          ).toLocaleDateString()}
                        </td>
                        <td>
                          {report.reportStatus === 1 ? "Success" : "Pending"}
                        </td>
                        <td>
                          <div className="button-group">
                            <button
                              title="View"
                              onClick={() => handleViewReport(report.reportId)}
                            >
                              <i className="fa fa-eye" aria-hidden="true"></i>
                            </button>
                            <button
                              title="Mail"
                              onClick={() =>
                                handleSendEmail(
                                  report.reportId,
                                  report.emailRecipients
                                )
                              }
                            >
                              <i
                                className="fa fa-envelope-o"
                                aria-hidden="true"
                              ></i>
                            </button>
                            <button
                              className="icon-btn"
                              title="Download"
                              onClick={() =>
                                handleDownload(
                                  report.reportId,
                                  report.reportName
                                )
                              }
                            >
                              <i
                                className="fa fa-download"
                                aria-hidden="true"
                              ></i>
                            </button>
                            <button
                              title="Delete"
                              disabled={
                                selectedReports.length !== 1 ||
                                selectedReports[0] !== report.reportId
                              }
                              onClick={() =>
                                handleOpenDeleteModal(
                                  filteredReports.find(
                                    (report) =>
                                      report.reportId === selectedReports[0]
                                  )
                                )
                              }
                            >
                              <i className="fa fa-trash" aria-hidden="true"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7">
                        <div className="nms-empty">
                          {localizationStrings?.noReports ||
                            localizationStrings?.noData}
                        </div>
                      </td>
                    </tr>
                  )}
                  {error && (
                    <tr>
                      <td
                        colSpan="7"
                        style={{ color: "red", textAlign: "center" }}
                      >
                        {error}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="pagination-container">
              {visibleReports.length > 0 ? (
                <>
                  <div className="pagination-info">
                    <p>
                      {localizationStrings?.showing}{" "}
                      {visibleReports.length === 0
                        ? 0
                        : currentPage * pageSize + 1}
                      -
                      {Math.min(
                        (currentPage + 1) * pageSize,
                        visibleReports.length
                      )}{" "}
                      {localizationStrings?.of} {visibleReports.length}{" "}
                      {localizationStrings?.records}
                    </p>
                  </div>
                  <div className="pagination">
                    <button
                      onClick={() => handlePageClick(0)}
                      disabled={currentPage === 0}
                    >
                      {localizationStrings?.firstPage}
                    </button>
                    <button
                      onClick={() => handlePageClick(currentPage - 1)}
                      disabled={currentPage === 0}
                    >
                      {localizationStrings?.previous}
                    </button>
                    {getPageNumbers().map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageClick(page)}
                        className={currentPage === page ? "active" : ""}
                      >
                        {page + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => handlePageClick(currentPage + 1)}
                      disabled={currentPage === totalPages - 1}
                    >
                      {localizationStrings?.next}
                    </button>
                    <button
                      onClick={() => handlePageClick(totalPages - 1)}
                      disabled={currentPage === totalPages - 1}
                    >
                      {localizationStrings?.lastPage}
                    </button>
                  </div>
                </>
              ) : (
                <div>
                  <p>{localizationStrings?.noReports}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal show={isModalOpen} onHide={closeModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{localizationStrings?.viewReport}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalData ? (
            <iframe
              src={modalData}
              title="Report PDF"
              style={{ width: "100%", height: "500px", border: "none" }}
            ></iframe>
          ) : (
            <p>{localizationStrings?.noReportDisplay}</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="text-btn primary-btn" onClick={closeModal}>
            {localizationStrings?.close}
          </button>
        </Modal.Footer>
      </Modal>
      <div
        className={`modal fade`}
        id="deleteReport"
        tabIndex="-1"
        aria-labelledby="deleteUrlLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content inc-modal">
            <div className="modal-body success-msg">
              <img src={errorIcon} alt="error" />
              <p>{localizationStrings?.deleteConfirmation}</p>
              <button
                className="text-btn primary-btn"
                data-bs-dismiss="modal"
                onClick={() => {
                  handleDeleteReport();
                }}
              >
                {localizationStrings?.yes}
              </button>
            </div>
          </div>
        </div>
      </div>
      <Modal
        show={isModalOpen1}
        onHide={handleCloseDeleteModal}
        centered
        className="delete-confirmation-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{localizationStrings?.deleteConfirmation}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {reportToDelete ? (
            <p>
              {localizationStrings?.reportDeleteInfo} "
              <strong>{reportName}</strong>"?
            </p>
          ) : (
            <p>{localizationStrings?.reportSelectDeleteInfo}</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            className="text-btn primary-btn"
            onClick={handleCloseDeleteModal}
          >
            {localizationStrings?.cancel}
          </button>
          <button className="text-btn primary-btn" onClick={handleDeleteReport}>
            {localizationStrings?.yes}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={isConfirmationModalOpen}
        onHide={handleCloseConfirmationModal}
        centered
      >
        <Modal.Header closeButton className="modal-header">
          <Modal.Title className="modal-title">
            {localizationStrings?.confirmEmailSend}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-body">
          <p className="modal-message">
            {localizationStrings?.reportSendInfo} <strong>{email}</strong>?
          </p>
        </Modal.Body>
        <Modal.Footer className="modal-footer">
          <button
            className="text-btn primary-btn"
            onClick={handleCloseConfirmationModal}
          >
            {localizationStrings?.cancel}
          </button>
          <button
            className="text-btn primary-btn"
            onClick={handleConfirmSendEmail}
          >
            {localizationStrings?.confirm}
          </button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showDeleteSuccessModal}
        onHide={() => setShowDeleteSuccessModal(false)}
        centered
        className="inc-alert index-2000"
      >
        <span className="success-icon">
          <i className="icon done"></i>
        </span>
        <h3>{localizationStrings?.success}</h3>
        <p>Report deleted successfully.</p>
        <button
          className="text-btn primary-btn"
          onClick={() => setShowDeleteSuccessModal(false)}
        >
          {localizationStrings?.ok}
        </button>
      </Modal>
    </div>
  );
}

export default Reports;
